import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { mocApi } from '../api/client';
import { fmtDate, CategoryBadge, StatusBadge } from '../lib/mocUtils';
import { STATUS_LABELS } from '../api/demoData';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileCsv, MicrosoftExcelLogo, Trash } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { DeleteMocDialog, isAdminGeneral } from '../components/DeleteMocDialog';

const COLUMNS = [
  ['id', 'N° MOC'], ['title', 'Titre'], ['site', 'Site'], ['department', 'Département'],
  ['category', 'Catégorie'], ['nature', 'Nature'], ['status', 'Statut'],
  ['requester', 'Demandeur'], ['createdAt', 'Créée le'], ['updatedAt', 'MAJ'],
];

export default function Register() {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = isAdminGeneral(user);
  const [toDelete, setToDelete] = useState(null);
  const load = () => mocApi.listMocs().then(setRows);

  useEffect(() => { load(); }, []);

  const toRow = (m) => COLUMNS.map(([k]) => {
    if (k === 'status') return STATUS_LABELS[m.status] || m.status;
    if (k === 'category') return m.category ? `Catégorie ${m.category}` : 'Non classée';
    if (k === 'createdAt' || k === 'updatedAt') return fmtDate(m[k]);
    return m[k] ?? '';
  });

  const download = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Export ${filename}`);
  };

  const exportCsv = () => {
    const header = COLUMNS.map(([, l]) => l).join(';');
    const body = rows.map((m) => toRow(m).map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
    download('\uFEFF' + header + '\n' + body, 'registre_moc.csv', 'text/csv;charset=utf-8;');
  };

  const exportExcel = () => {
    // Excel-compatible HTML table (.xls) — no extra dependency needed.
    const th = COLUMNS.map(([, l]) => `<th>${l}</th>`).join('');
    const trs = rows.map((m) => `<tr>${toRow(m).map((v) => `<td>${v}</td>`).join('')}</tr>`).join('');
    const html = `<html><head><meta charset="utf-8"></head><body><table border="1">${`<tr>${th}</tr>`}${trs}</table></body></html>`;
    download(html, 'registre_moc.xls', 'application/vnd.ms-excel');
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Traçabilité complète</div>
          <h1 className="font-display text-3xl font-black tracking-tight mt-1">Registre MOC</h1>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" data-testid="export-csv" onClick={exportCsv}><FileCsv size={16} className="mr-1.5" />Export CSV</Button>
          <Button size="sm" variant="outline" data-testid="export-excel" onClick={exportExcel}><MicrosoftExcelLogo size={16} className="mr-1.5" />Export Excel</Button>
        </div>
      </div>

      <Card className="rounded-sm p-0 overflow-x-auto">
        <table className="w-full text-sm moc-table min-w-[900px]">
          <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {COLUMNS.map(([k, l]) => <th key={k} className="text-left font-semibold px-4 py-3 whitespace-nowrap">{l}</th>)}
              {canDelete && <th className="px-4 py-3" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((m) => (
              <tr key={m.id} data-testid={`register-row-${m.id}`} onClick={() => navigate(`/moc/${m.id}`)} className="cursor-pointer hover:bg-muted/40">
                <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{m.id}</td>
                <td className="px-4 py-3 font-medium max-w-xs truncate">{m.title}</td>
                <td className="px-4 py-3 whitespace-nowrap">{m.site}</td>
                <td className="px-4 py-3 whitespace-nowrap">{m.department}</td>
                <td className="px-4 py-3"><CategoryBadge category={m.category} /></td>
                <td className="px-4 py-3 whitespace-nowrap">{m.nature}</td>
                <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
                <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{m.requester}</td>
                <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmtDate(m.createdAt)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmtDate(m.updatedAt)}</td>
                {canDelete && (
                  <td className="px-2 py-3">
                    <button data-testid={`register-delete-${m.id}`} title="Supprimer la MOC"
                      onClick={(e) => { e.stopPropagation(); setToDelete(m); }}
                      className="p-1.5 rounded-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                      <Trash size={16} weight="bold" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <DeleteMocDialog moc={toDelete} open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}
        onDone={() => { setRows((r) => r.filter((x) => x.id !== toDelete?.id)); load(); }} />
    </div>
  );
}
